/**
 * Keyboard Layout Conversion Utilities
 *
 * Converts Cyrillic characters typed on wrong keyboard layout to their
 * English QWERTY equivalents based on physical key positions.
 *
 * Use case: User types "ВУЧУ" (Russian layout) but meant "DEXE" (English layout).
 * This utility converts the input automatically so search works correctly.
 */

/**
 * Mapping of Cyrillic characters to their QWERTY English equivalents.
 * Based on physical key positions on standard Russian ЙЦУКЕН keyboard.
 */
const CYRILLIC_TO_QWERTY: Record<string, string> = {
  // Row 1: йцукенгшщзхъ → qwertyuiop[]
  й: 'q',
  ц: 'w',
  у: 'e',
  к: 'r',
  е: 't',
  н: 'y',
  г: 'u',
  ш: 'i',
  щ: 'o',
  з: 'p',
  х: '[',
  ъ: ']',

  // Row 2: фывапролджэ → asdfghjkl;'
  ф: 'a',
  ы: 's',
  в: 'd',
  а: 'f',
  п: 'g',
  р: 'h',
  о: 'j',
  л: 'k',
  д: 'l',
  ж: ';',
  э: "'",

  // Row 3: ячсмитьбю → zxcvbnm,.
  я: 'z',
  ч: 'x',
  с: 'c',
  м: 'v',
  и: 'b',
  т: 'n',
  ь: 'm',
  б: ',',
  ю: '.',

  // Ukrainian-specific characters
  і: 's', // Same position as ы on Ukrainian keyboard
  ї: ']', // Same position as ъ
  є: "'", // Same position as э
  ґ: ']', // Alternate position
};

/**
 * Converts text from Cyrillic keyboard layout to English QWERTY layout.
 * Preserves case and leaves non-Cyrillic characters unchanged.
 *
 * @param text - Input text that may contain Cyrillic characters
 * @returns Text with Cyrillic characters converted to their QWERTY equivalents
 *
 * @example
 * toEnglishLayout('ВУЧУ') // Returns 'DEXE'
 * toEnglishLayout('руддщ') // Returns 'hello'
 * toEnglishLayout('BTC123') // Returns 'BTC123' (unchanged)
 */
export function toEnglishLayout(text: string): string {
  return text
    .split('')
    .map(char => {
      const lowerChar = char.toLowerCase();
      const mappedChar = CYRILLIC_TO_QWERTY[lowerChar];

      if (!mappedChar) {
        // Not a Cyrillic character, return as-is
        return char;
      }

      // Preserve original case
      return char === lowerChar ? mappedChar : mappedChar.toUpperCase();
    })
    .join('');
}
